import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faClock,
  faLocationDot,
  faMagnifyingGlass,
  faMap,
  faReceipt,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import './DeliveryConditionsPage.scss'
import { useGetLocationsQuery } from '../../store/api/locationsApi'
import { ILocation } from '../../types/locations'
import { getGeneralInfo } from '../../utils/generalInfo'
import Loader from '../../components/loader/Loader'

const c = 'delivery-conditions-page'

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const DeliveryConditionsPage = () => {
  const generalInfo = getGeneralInfo()
  const { t, i18n } = useTranslation()
  const { data: locations = [], isLoading } = useGetLocationsQuery()
  const [query, setQuery] = useState('')

  const getTranslatedName = (item: ILocation) => {
    if (i18n.language === 'ru' && item?.label_ru) {
      return item.label_ru
    }

    return item.label
  }

  const filtered = useMemo(() => {
    const q = normalize(query.trim())
    if (!q) return locations

    return locations.filter((location: ILocation) =>
      normalize(`${location.label} ${location.label_ru ?? ''}`).includes(q),
    )
  }, [locations, query])

  const titleRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return (
    <div className={c}>
      <h1 className={`${c}__title`} ref={titleRef}>{t('DELIVERY_CONDITIONS')}</h1>

      <div className={`${c}__cards`}>
        <div className={`${c}__card`}>
          <span className={`${c}__card-icon`}>
            <FontAwesomeIcon icon={faClock} />
          </span>
          <Trans
            i18nKey='DELIVERY_SCHEDULE'
            values={{
              minTime: generalInfo?.minTime,
              maxTime: generalInfo?.maxTime,
            }}
          />
        </div>
        {!!generalInfo?.minPrice && (
          <div className={`${c}__card`}>
            <span className={`${c}__card-icon`}>
              <FontAwesomeIcon icon={faReceipt} />
            </span>
            <Trans
              i18nKey='MINIMUM_PRICE'
              values={{ minPrice: generalInfo.minPrice }}
            />
          </div>
        )}
      </div>

      <div className={`${c}__subtitle`}>{t('LOCATION_PRICE')}</div>

      <label className={`${c}__search`}>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('SEARCH')}
          enterKeyHint='search'
        />
        {query && (
          <button aria-label={t('CLEAR_SEARCH')} onClick={() => setQuery('')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </label>

      {isLoading ? (
        <Loader size={44} />
      ) : filtered.length ? (
        <div className={`${c}__locations`}>
          {filtered.map((location: ILocation, index: number) => (
            <div
              key={location._id}
              className={`${c}__location`}
              style={{ '--i': Math.min(index, 12) } as React.CSSProperties}
            >
              <span className={`${c}__location-label`}>
                <FontAwesomeIcon icon={faLocationDot} /> {getTranslatedName(location)}
              </span>
              <span
                className={`${c}__location-value${
                  Number(location.delivery_price) === 0 ? ' is-free' : ''
                }`}
              >
                {location.delivery_price} lei
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className={`${c}__empty`}>
          <FontAwesomeIcon icon={faMap} className={`${c}__empty-icon`} />
          {t('NO_RESULTS_TITLE')}
        </div>
      )}
    </div>
  )
}

export default DeliveryConditionsPage
